import { useIsFetching } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FileDown, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

export const exportDTO = z.object({
  id: z.string().uuid(),
  base64: z.string().base64(),
  type: z.enum(['csv', 'xlsx']),
})

type Export = z.infer<typeof exportDTO>

type ExportProps = {
  route: string
}

export function useDownloadExport({ route }: ExportProps) {
  const { t } = useTranslation('common')

  const isDownloadingExport =
    useIsFetching({
      queryKey: [route],
      exact: false,
    }) > 0

  const downloadExport = async (
    params: Record<string, unknown>,
    type: Export['type'],
  ) => {
    await queryClient
      .fetchQuery<Export, AxiosError<ApiError>>({
        queryKey: [route.concat(`/${type}`), params],
        queryFn: () =>
          api.backend.get(route, {
            params: {
              limit: 25,
              ...params,
            },
          }),
      })
      .then((data) => {
        const link = document.createElement('a')

        link.href = `data:${data.type};base64,${data.base64}`
        link.download = `table.${data.type}`

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)

        toast(Toast, {
          data: {
            type: 'success',
            icon: FileDown,
            title: t('use_download_export.toast_success.title'),
            description: t('use_download_export.toast_success.description'),
          },
        })
      })
      .catch((error) => {
        toast(Toast, {
          data: {
            type: 'danger',
            icon: OctagonAlert,
            title: t('use_download_export.toast_error.title'),
            description:
              error?.response?.data?.friendlyMessage ||
              t('use_download_export.toast_error.description'),
          },
        })
      })
  }

  return { downloadExport, isDownloadingExport }
}
